/**
 * MCB GTM DataLayer Bridge
 *
 * Listens to Concept theme CustomEvents and pushes GA4-shaped ecommerce
 * objects to window.dataLayer for consumption by Google Tag Manager.
 *
 * Event naming: GA4 standard names prefixed with mcb_
 *
 * Enrichment: fields not carried by theme events (vendor, type,
 * external_id metafields, product_id) are read from the
 * #mcb-product-data JSON block rendered by mcb-gtm-datalayer.liquid.
 * That block is product-page only; enrichment falls back to empty
 * strings on other page types.
 *
 * Theme events used (Concept v5.3.1):
 *   page:loaded, variant:change, ajaxProduct:added, ajaxProduct:error,
 *   cart:updated, quickview:open
 */
(function () {
  'use strict';

  // ─── State ──────────────────────────────────────────────────────────────────
  // window.dataLayer is guaranteed by mcb-gtm-datalayer.liquid before this runs.

  var _productData = null;        // false = parsed but no block found; null = not yet parsed
  var _prevCart = null;           // cached previous cart for remove_from_cart diffing
  var _variantUnsub = null;       // pubsub unsubscriber — prevents duplicate subscriptions on AJAX nav

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Parse and cache the #mcb-product-data block.
   * Returns the parsed object, or false if the block is absent.
   */
  function getProductData() {
    if (_productData !== null) return _productData;
    var el = document.getElementById('mcb-product-data');
    if (!el) { _productData = false; return false; }
    try { _productData = JSON.parse(el.textContent); } catch (e) { _productData = false; }
    return _productData;
  }

  /**
   * O(1) variant lookup from the enrichment hash-map.
   * Returns the enrichment object or null.
   */
  function findVariant(variantId) {
    var pd = getProductData();
    return (pd && pd.variants && pd.variants[String(variantId)]) || null;
  }

  function getCurrency() {
    var pd = getProductData();
    return (pd && pd.currency)
      || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      || 'DKK';
  }

  function getShopName() {
    var pd = getProductData();
    return (pd && pd.shop_name) || '';
  }

  // ─── Item builder ────────────────────────────────────────────────────────────
  /**
   * Build a GA4 item object from a variant-like source object.
   *
   * @param {object} v        - Variant (from event), cart line item, or cart-add response item.
   *                            Accepts both theme event shape and Shopify cart API shape.
   * @param {object} enriched - Optional enrichment from #mcb-product-data (external_id, product_id, sku).
   * @param {object} opts     - Optional overrides: quantity, coupon, index, item_list_id, item_list_name.
   */
  function buildItem(v, enriched, opts) {
    opts = opts || {};
    var ev = enriched || {};
    var pd = getProductData();
    var product = pd && pd.product;

    return {
      // GA4 required
      item_id:    String(v.id || v.variant_id || ''),
      item_name:  v.product_title || (product && product.title) || v.title || '',

      // GA4 recommended
      item_brand:     v.vendor       || (product && product.vendor) || '',
      item_category:  v.product_type || (product && product.type)   || '',
      item_variant:   v.variant_title || v.title || '',
      price:          (v.price || 0) / 100,
      quantity:       v.quantity || opts.quantity || 1,
      affiliation:    getShopName(),

      // GA4 optional — populated when available
      discount:       v.total_discount ? v.total_discount / 100 : undefined,
      coupon:         opts.coupon       || undefined,
      index:          opts.index !== undefined ? opts.index : undefined,
      item_list_id:   opts.item_list_id   || undefined,
      item_list_name: opts.item_list_name || undefined,

      // MCB custom parameters — map these to GA4 custom dimensions in GTM
      product_id:          String(v.product_id || ev.product_id || (product && product.id) || ''),
      item_sku:                 v.sku || ev.sku || '',
      external_id:         ev.external_id || '',
      product_external_id: (product && product.external_id) || ''
    };
  }

  // ─── Cart line item builder (from /cart.js response) ───────────────────────
  /**
   * Simpler builder for cart line items (no enrichment lookup available
   * — enriched data is product-page only).
   */
  function cartLineToItem(item, index) {
    return {
      item_id:       String(item.variant_id || item.id || ''),
      item_name:     item.product_title || item.title || '',
      item_brand:    item.vendor        || '',
      item_category: item.product_type  || '',
      item_variant:  item.variant_title || '',
      price:         (item.price || 0) / 100,
      quantity:      item.quantity || 1,
      affiliation:   getShopName(),
      discount:      item.total_discount ? item.total_discount / 100 : undefined,
      index:         index,
      product_id:    String(item.product_id || ''),
      item_sku:           item.sku || '',
      external_id:   '',
      product_external_id: ''
    };
  }

  // ─── page:loaded ────────────────────────────────────────────────────────────
  /**
   * mcb_page_view  — fires on every page.
   * mcb_view_item  — fires on product pages for the initially selected variant,
   *                  reading the theme's existing [data-selected-variant] block.
   *
   * Also sets up the variant-change subscription here because theme.pubsub is
   * not yet initialised when this IIFE runs (our script is deferred before
   * vendor.js / theme.js). By page:loaded, theme.js has fully executed.
   *
   * NOTE: the theme dispatches `variant:change` on `this.productForm` (not on
   * document) without bubbles:true, so document.addEventListener('variant:change')
   * never fires. We subscribe to theme.pubsub instead, which is always published.
   */
  function onPageLoaded() {
    // Seed _prevCart on cart/checkout pages so mcb_begin_checkout fires even
    // when the user lands directly without triggering a cart:updated event.
    if (!_prevCart && document.body.classList.contains('template-cart')) {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { if (!_prevCart) _prevCart = cart; })
        .catch(function () {});
    }

    // GA4: page_view
    window.dataLayer.push({
      event:         'mcb_page_view',
      page_title:    document.title,
      page_location: window.location.href,
      page_path:     window.location.pathname,
      page_type:     (document.body.className.match(/template-(\S+)/) || [])[1] || ''
    });

    // GA4: view_item — initial variant
    // Primary: read theme's [data-selected-variant] block (present when the variant
    // picker renders — i.e. multi-variant products with a swatch/button/dropdown).
    // Fallback: single-variant products have no picker and therefore no
    // [data-selected-variant]. Read from our own enrichment block instead,
    // preferring the URL ?variant= param then the first (only) variant.
    var pd = getProductData();
    if (pd) {
      try {
        var variant = null;
        var enriched = null;
        var el = document.querySelector('.product-form [data-selected-variant]')
              || document.querySelector('[data-selected-variant]');

        if (el) {
          // Multi-variant path — theme has rendered the picker block
          variant = JSON.parse(el.textContent);
          enriched = findVariant(variant.id);
        } else if (pd.variants) {
          // Single-variant fallback — resolve from URL ?variant= or first entry
          var urlVariantId = new URLSearchParams(window.location.search).get('variant');
          enriched = (urlVariantId && findVariant(urlVariantId))
                  || pd.variants[Object.keys(pd.variants)[0]];
          if (enriched) {
            var vid = urlVariantId || Object.keys(pd.variants)[0];
            // Build a minimal variant-like object from enrichment data
            variant = {
              id:           vid,
              variant_id:   vid,
              product_id:   enriched.product_id,
              price:        enriched.price || 0,
              sku:          enriched.sku || '',
              vendor:       pd.product && pd.product.vendor,
              product_type: pd.product && pd.product.type,
              product_title: pd.product && pd.product.title
            };
          }
        }

        if (variant) {
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: 'mcb_view_item',
            ecommerce: {
              currency: getCurrency(),
              value:    (variant.price || 0) / 100,
              items:    [buildItem(variant, enriched, { quantity: 1 })]
            }
          });
        }
      } catch (e) {}
    }

    // ── variant:change → mcb_view_item ────────────────────────────────────────
    // GA4: view_item — fires each time the user selects a different variant.
    // Uses theme.pubsub (always published) instead of the variant:change
    // CustomEvent (dispatched on the form element, does not bubble to document).
    // Payload shape: { data: { variant, sectionId, productId, parsedHTML } }
    //
    // Unsubscribe first to prevent stacking handlers on AJAX page navigation
    // (theme fires page:loaded on every transition; pubsub.subscribe appends).
    if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
      if (_variantUnsub) { _variantUnsub(); }
      _variantUnsub = window.theme.pubsub.subscribe(
        window.theme.pubsub.PUB_SUB_EVENTS.variantChange,
        function (payload) {
          var variant = payload && payload.data && payload.data.variant;
          if (!variant) return;
          var enriched = findVariant(variant.id);
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: 'mcb_view_item',
            ecommerce: {
              currency: getCurrency(),
              value:    (variant.price || 0) / 100,
              items:    [buildItem(variant, enriched, { quantity: 1 })]
            }
          });
        }
      );
    }
  }

  document.addEventListener('page:loaded', onPageLoaded);

  // ─── ajaxProduct:added → mcb_add_to_cart ────────────────────────────────────
  /**
   * GA4: add_to_cart.
   * The /cart/add.js response includes product_title, vendor, product_type,
   * variant_title, sku, product_id — so item_brand and item_category are
   * populated directly from the response without needing enrichment.
   * external_id comes from the enrichment lookup (available when the add-to-cart
   * happens on the product page).
   */
  document.addEventListener('ajaxProduct:added', function (evt) {
    var detail = evt.detail && evt.detail.product;
    if (!detail) return;
    // Shopify's /cart/add.js returns { items: [...] } with Accept: application/json,
    // but the theme uses Accept: application/javascript which returns the flat line
    // item directly (no items wrapper). Handle all three shapes:
    //   1. { items: [...] }  — standard JSON format
    //   2. { item: {...} }   — some themes wrap in .item
    //   3. { variant_id, price, ... } — flat line item (application/javascript response)
    var items = detail.items
      || (detail.item ? [detail.item] : null)
      || (detail.variant_id ? [detail] : []);
    if (!items.length) return;

    var gaItems = items.map(function (item) {
      var enriched = findVariant(item.variant_id || item.id);
      return buildItem(item, enriched);
    });

    var value = items.reduce(function (sum, item) {
      return sum + (item.line_price || (item.price * (item.quantity || 1)) || 0);
    }, 0) / 100;

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'mcb_add_to_cart',
      ecommerce: {
        currency: getCurrency(),
        value:    value,
        items:    gaItems
      }
    });
  });

  // ─── ajaxProduct:error → mcb_add_to_cart_error ──────────────────────────────
  document.addEventListener('ajaxProduct:error', function (evt) {
    window.dataLayer.push({
      event:         'mcb_add_to_cart_error',
      error_message: (evt.detail && evt.detail.errorMessage) || 'Unknown error'
    });
  });

  // ─── cart:updated → mcb_view_cart + mcb_remove_from_cart ────────────────────
  /**
   * GA4: view_cart  — fires after every cart mutation.
   * GA4: remove_from_cart — fires when items are removed or quantity reduced,
   *      detected by diffing against the previous cart state.
   *
   * The /cart.js response includes per-line: variant_id, product_title,
   * variant_title, vendor, product_type, sku, product_id, price, quantity,
   * total_discount, key.
   */
  document.addEventListener('cart:updated', function (evt) {
    var cart = evt.detail && evt.detail.cart;
    if (!cart) return;
    var currency = cart.currency || getCurrency();
    var cartItems = cart.items || [];

    // GA4: view_cart
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'mcb_view_cart',
      ecommerce: {
        currency: currency,
        value:    (cart.total_price || 0) / 100,
        items:    cartItems.map(cartLineToItem)
      }
    });

    // GA4: remove_from_cart — diff with previous cart state
    if (_prevCart) {
      var removed = [];
      (_prevCart.items || []).forEach(function (prev) {
        var curr = cartItems.find(function (i) { return i.key === prev.key; });
        if (!curr) {
          // Line item fully removed
          removed.push(Object.assign({}, prev));
        } else if (curr.quantity < prev.quantity) {
          // Quantity reduced — report the delta
          removed.push(Object.assign({}, prev, { quantity: prev.quantity - curr.quantity }));
        }
      });

      if (removed.length) {
        var removedValue = removed.reduce(function (sum, item) {
          return sum + ((item.price || 0) * (item.quantity || 1));
        }, 0) / 100;

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'mcb_remove_from_cart',
          ecommerce: {
            currency: currency,
            value:    removedValue,
            items:    removed.map(cartLineToItem)
          }
        });
      }
    }

    _prevCart = cart;
  });

  // ─── begin_checkout ──────────────────────────────────────────────────────────
  /**
   * GA4: begin_checkout — fires when a checkout button is clicked.
   * Uses _prevCart (populated by the first cart:updated event) for cart contents.
   * Both checkout buttons in this theme use name="checkout" (cart drawer + cart page).
   */
  document.addEventListener('click', function (e) {
    if (!_prevCart) return;
    if (!e.target.closest('[name="checkout"]')) return;

    var cart     = _prevCart;
    var currency = cart.currency || getCurrency();
    var discounts = cart.cart_level_discount_applications || [];
    var coupon   = discounts.length ? discounts[0].title : undefined;

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'mcb_begin_checkout',
      ecommerce: {
        currency: currency,
        value:    (cart.total_price || 0) / 100,
        coupon:   coupon,
        items:    (cart.items || []).map(cartLineToItem)
      }
    });
  });

  // ─── quickview:open → mcb_quickview_open ────────────────────────────────────
  /**
   * mcb_quickview_open — fires when a quickview modal opens.
   */
  document.addEventListener('quickview:open', function (evt) {
    window.dataLayer.push({
      event:        'mcb_quickview_open',
      content_type: 'product',
      product_url:  (evt.detail && evt.detail.productUrl) || ''
    });
  });


})();
